import { RefObject, useEffect, useRef } from 'react';

const POSITION_OBSERVER_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1] as const;

/**
 * When `isOpen`, observes the trigger element's vertical position in the viewport.
 * Calls `onClose` when `top` changes (e.g. user scrolled the chat).
 */
export function useCloseOnChatBoxScroll(
  triggerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void
): void {
  const positionRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const el = triggerRef.current;
    positionRef.current = null;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry) return;
        const { top } = entry.boundingClientRect;
        const prev = positionRef.current;
        if (prev !== null && prev !== top) {
          onClose();
          return;
        }
        positionRef.current = top;
      },
      { threshold: [...POSITION_OBSERVER_THRESHOLDS], root: null }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen, onClose, triggerRef]);
}
